import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { FaArrowLeft, FaPhone, FaEnvelope, FaMapMarkerAlt, FaCheckCircle, FaGasPump, FaTachometerAlt, FaCar, FaCalendarAlt, FaPalette, FaCogs, FaDollarSign, FaShieldAlt, FaClipboardList, FaCalculator, FaHeart, FaShare, FaTruck, FaWrench, FaRoad } from 'react-icons/fa';
import { FiHeart, FiShare2, FiPhone, FiMail, FiCheckCircle, FiMapPin, FiClock, FiX, FiArrowLeft, FiLoader, FiEdit } from 'react-icons/fi';
import ImageGallery from '../components/ImageGallery';
import LeadForm from '../components/LeadForm';
import CalendarScheduler from '../components/CalendarScheduler';
import PaymentModal from '../components/PaymentModal';
import thirdPartyIntegrations from '../services/thirdPartyIntegrations';
import useLocomotiveScroll from '../hooks/useLocomotiveScroll';
import { useAdvancedGsap } from '../hooks/useAdvancedGsap';
import FinancingCalculator from '../components/ai/FinancingCalculator';
import VehicleQA from '../components/ai/VehicleQA';
import openaiService from '../services/openaiService';
import { vehicleService } from '../services/vehicleService';
import { extractVehicleId, generateVehicleSlug } from '../utils/vehicleSlug';
import { canonicalizeUrl } from '../utils/canonicalUrl';

const VehicleDetail = () => {
  const { id: slugOrId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeCode = searchParams.get('store') || '0226';
  // Extract the actual ID from slug (supports both /vehicle/30 and /vehicle/2024-ford-f150-30)
  const id = extractVehicleId(slugOrId);
  console.log('VehicleDetail component mounted with slug:', slugOrId, 'extracted ID:', id);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const containerRef = useRef(null);
  const heroRef = useRef(null);

  // Initialize smooth scroll and GSAP
  useLocomotiveScroll();
  useAdvancedGsap();

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  // Parallax transforms
  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const y = useSpring(useTransform(scrollYProgress, [0, 1], [0, -200]), springConfig);
  const yText = useSpring(useTransform(scrollYProgress, [0, 1], [0, -100]), springConfig);
  const scale = useSpring(useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.05, 0.95]), springConfig);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1, 0.7]);

  const [vehicle, setVehicle] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [inquiryType, setInquiryType] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState('deposit'); // 'deposit' or 'full'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leadFormData, setLeadFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    inquiryType: 'test-drive',
  });
  const [aiDescription, setAiDescription] = useState(null);
  const [loadingDescription, setLoadingDescription] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [carfaxReport, setCarfaxReport] = useState(null);
  const [vehicleValuation, setVehicleValuation] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadVehicle();
  }, [id]);

  const loadVehicle = async () => {
    console.log('VehicleDetail: Loading vehicle with ID:', id);
    setLoading(true);
    setError(null);

    try {
      const result = await vehicleService.getVehicleById(id);
      console.log('VehicleDetail: Raw result from vehicleService:', result);

      // Handle both possible response formats
      const data = result?.data || result;
      const error = result?.error;

      if (error) {
        console.error('Error loading vehicle:', error);
        setError('Failed to load vehicle');
        return;
      }

      if (!data) {
        console.error('No vehicle data found for ID:', id);
        setError('Vehicle not found');
        return;
      }

      console.log('VehicleDetail: Loaded vehicle:', data);
      setVehicle(data);

      // Redirect old /vehicle/ID URLs to new SEO-friendly /vehicle/year-make-model-ID format
      const expectedSlug = generateVehicleSlug(data);
      if (slugOrId !== expectedSlug && /^\d+$/.test(slugOrId)) {
        // Old URL format detected, redirect to SEO-friendly URL
        navigate(`/vehicle/${expectedSlug}`, { replace: true });
        return;
      }

      // Generate AI description if not already in the vehicle data
      if (data && (!data.description || data.description.length < 100)) {
        generateAiDescription(data);
      }
    } catch (err) {
      console.error('Exception loading vehicle:', err);
      setError('Failed to load vehicle');
    } finally {
      setLoading(false);
    }
  };

  const generateAiDescription = async (vehicleData) => {
    setLoadingDescription(true);
    try {
      const { description, error } = await openaiService.generateVehicleDescription(vehicleData);
      if (!error && description) {
        setAiDescription(description);
      }
    } catch (err) {
      console.error('Error generating description:', err);
    } finally {
      setLoadingDescription(false);
    }
  };

  const handleLeadFormSubmit = (e) => {
    e.preventDefault();
    console.log('Lead form submitted:', leadFormData);
    alert('Thank you for your interest! We will contact you soon.');
    setShowLeadForm(false);
    setLeadFormData({
      name: '',
      email: '',
      phone: '',
      message: '',
      inquiryType: 'test-drive',
    });
  };

  const handleLeadFormChange = (e) => {
    setLeadFormData({
      ...leadFormData,
      [e.target.name]: e.target.value,
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  const calculateMonthlyPayment = (price) => {
    // Simple calculation for demo: price / 72 months at 4.9% APR
    const principal = price * 0.8; // Assuming 20% down
    const monthlyRate = 4.9 / 100 / 12;
    const months = 72;
    const payment = (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1);
    return Math.round(payment);
  };

  // Always show something to debug
  console.log('Render state:', { loading, error, vehicle, id });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading vehicle details for ID: {id}...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Vehicle Not Found</h1>
            <p className="text-gray-600 mb-6">
              {error || 'The vehicle you\'re looking for doesn\'t exist or may have been sold.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/vehicles"
                className="inline-flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                <FiArrowLeft className="w-4 h-4" />
                <span>Back to Inventory</span>
              </Link>
              <button
                onClick={loadVehicle}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const vehicleImages = vehicle.images && vehicle.images.length > 0
    ? vehicle.images
    : ['https://images.unsplash.com/photo-1494976688153-d4d4c0c8e0b0?w=800&h=600&fit=crop'];

  // Strip language that conflicts with dealer VLA requirements
  const sanitizeDescription = (desc) => {
    if (!desc) return desc;
    return desc
      .replace(/private\s+sale\.?/gi, '')
      .replace(/you\s+will\s+not\s+pay\s+us\s+any\s+taxes\s+or\s+fees\.?/gi, '')
      .replace(/no\s+taxes\s+(or|and)\s+no\s+fees\.?/gi, '')
      .replace(/no\s+taxes\s+or\s+fees\.?/gi, '')
      .replace(/tax[- ]?free\.?/gi, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const displayDescription = sanitizeDescription(aiDescription || vehicle.description) || 'No description available for this vehicle.';

  return (
    <div className="min-h-screen bg-gray-50" data-scroll-container ref={containerRef}>
      {vehicle && (
        <Helmet>
          <title>{`${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''} for Sale in Paradise, TX | Paradise-Performance`}</title>
          <meta name="description" content={`Buy this ${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''} with ${vehicle.mileage ? vehicle.mileage.toLocaleString() + ' miles' : 'low mileage'} for $${vehicle.price?.toLocaleString() || 'Call for price'}. ${vehicle.condition === 'professional' ? 'professional Pre-Owned' : vehicle.condition === 'new' ? 'Brand New' : 'Quality Used'} vehicle in Paradise, TX. Financing available.`} />

          {/* Open Graph / Facebook */}
          <meta property="og:type" content="product" />
          <meta property="og:url" content={canonicalizeUrl(`/vehicle/${generateVehicleSlug(vehicle)}`)} />
          <meta property="og:title" content={`${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''} - $${vehicle.price?.toLocaleString() || 'Call for price'}`} />
          <meta property="og:description" content={`${vehicle.mileage ? vehicle.mileage.toLocaleString() + ' miles' : 'Mileage N/A'} • ${vehicle.transmission || 'Transmission N/A'} • ${vehicle.condition || 'Used'}`} />
          <meta property="og:image" content={vehicle.images?.[0] || vehicle.image || ''} />
          <meta property="product:price:amount" content={vehicle.price || 0} />
          <meta property="product:price:currency" content="USD" />

          {/* Twitter */}
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:title" content={`${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`} />
          <meta property="twitter:description" content={`For Sale: $${vehicle.price?.toLocaleString() || 'Call'} • ${vehicle.mileage ? vehicle.mileage.toLocaleString() + ' miles' : 'Mileage N/A'}`} />
          <meta property="twitter:image" content={vehicle.images?.[0] || vehicle.image || ''} />

          {/* Canonical URL */}
          <link rel="canonical" href={canonicalizeUrl(`/vehicle/${generateVehicleSlug(vehicle)}`)} />

          {/* Structured Data - Vehicle Schema (VLA-compliant) */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Car",
              "name": `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`,
              "url": canonicalizeUrl(`/vehicle/${generateVehicleSlug(vehicle)}`),
              "brand": {
                "@type": "Brand",
                "name": vehicle.make || ''
              },
              "manufacturer": {
                "@type": "Organization",
                "name": vehicle.make || ''
              },
              "model": vehicle.model || '',
              "vehicleModelDate": String(vehicle.year || ''),
              "modelDate": String(vehicle.year || ''),
              "vehicleIdentificationNumber": vehicle.vin || '',
              "mileageFromOdometer": {
                "@type": "QuantitativeValue",
                "value": vehicle.mileage || 0,
                "unitCode": "SMI"
              },
              "itemCondition": vehicle.condition === 'new' ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition',
              ...(vehicle.transmission && { "vehicleTransmission": vehicle.transmission }),
              ...(vehicle.fuelType && { "fuelType": vehicle.fuelType }),
              ...(vehicle.exteriorColor && { "color": vehicle.exteriorColor }),
              ...(vehicle.drivetrain && { "driveWheelConfiguration": vehicle.drivetrain }),
              ...(vehicle.engine && { "vehicleEngine": { "@type": "EngineSpecification", "name": vehicle.engine } }),
              ...(vehicle.doors && { "numberOfDoors": vehicle.doors }),
              "offers": {
                "@type": "Offer",
                "priceCurrency": "USD",
                "price": vehicle.price || 0,
                "availability": "https://schema.org/InStock",
                "itemCondition": vehicle.condition === 'new' ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition',
                "url": canonicalizeUrl(`/vehicle/${generateVehicleSlug(vehicle)}`),
                "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                "offeredBy": {
                  "@type": "AutoDealer",
                  "@id": "https://paradise-performance.com/#dealer",
                  "name": "Paradise-Performance",
                  "url": "https://paradise-performance.com",
                  "telephone": "(817) 382-2316",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "305 Co Rd 3470",
                    "addressLocality": "Paradise",
                    "addressRegion": "TX",
                    "postalCode": "76073",
                    "addressCountry": "US"
                  }
                }
              },
              "image": vehicle.images?.length > 0 ? vehicle.images : [vehicle.image || ''],
              "description": sanitizeDescription(vehicle.description) || '',
              "sku": vehicle.vin || vehicle.stockNumber || String(vehicle.id)
            })}
          </script>
        </Helmet>
      )}

      <div className="px-4 lg:px-6 xl:px-8 2xl:px-12 max-w-[2400px] mx-auto">
        {/* Breadcrumb */}
        <nav className="mb-3 pt-2">
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            <li><Link to="/" className="hover:text-primary-600">Home</Link></li>
            <li>/</li>
            <li><Link to="/vehicles" className="hover:text-primary-600">Vehicles</Link></li>
            <li>/</li>
            <li className="text-gray-900">{vehicle.year} {vehicle.make} {vehicle.model}</li>
          </ol>
        </nav>

        {/* VLA-Required Info Bar — dealership, title, price, VIN, mileage, availability ABOVE THE FOLD */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                <FiMapPin className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" />
                <span className="font-medium text-gray-700">Paradise-Performance</span>
                <span className="text-gray-300">|</span>
                <span>Paradise, TX 76073</span>
              </div>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </h1>
              {/* Mobile-only price — visible above the fold on small screens */}
              <div className="flex items-center gap-3 mt-2 lg:hidden">
                <span className="text-2xl font-bold text-gray-900">
                  {vehicle.price ? `$${vehicle.price.toLocaleString()}` : 'Call for Price'}
                </span>
                <span className="text-xs text-gray-500">+ tax, title & fees</span>
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                {vehicle.mileage && (
                  <span className="flex items-center gap-1.5">
                    <FaTachometerAlt className="w-3.5 h-3.5 text-gray-400" />
                    <span className="font-medium">{vehicle.mileage.toLocaleString()} miles</span>
                  </span>
                )}
                {vehicle.vin && (
                  <span className="flex items-center gap-1.5">
                    <FaClipboardList className="w-3.5 h-3.5 text-gray-400" />
                    <span className="font-mono text-xs">{vehicle.vin}</span>
                  </span>
                )}
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${(vehicle.status === 'on-lot' || !vehicle.status) ? 'bg-green-100 text-green-800' :
                    vehicle.status === 'in-transit' ? 'bg-yellow-100 text-yellow-800' :
                      vehicle.status === 'pending-contract' ? 'bg-indigo-100 text-indigo-800' :
                        'bg-gray-100 text-gray-800'
                  }`}>
                  {vehicle.status === 'on-lot' ? 'In Stock' :
                    vehicle.status === 'in-transit' ? 'In Transit' :
                      vehicle.status === 'pending-contract' ? 'Sale Pending' :
                        vehicle.status || 'In Stock'}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${vehicle.condition === 'new' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                  {vehicle.condition === 'new' ? 'New' : 'Pre-Owned'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges — 2x2 grid above the fold */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <FaShieldAlt className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[12px] font-bold text-gray-900 leading-tight">90-Day / 3,000-Mile Warranty</p>
              <p className="text-[10px] text-gray-500 leading-tight mt-0.5">Powertrain coverage included</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <FaWrench className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[12px] font-bold text-gray-900 leading-tight">We're Mechanics Who Sell Cars</p>
              <p className="text-[10px] text-gray-500 leading-tight mt-0.5">Inspected by the techs who fix them</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
              <FaCheckCircle className="w-4 h-4 text-amber-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[12px] font-bold text-gray-900 leading-tight">
                {(() => {
                  const make = (vehicle.make || '').toLowerCase();
                  if (make === 'volkswagen' || make === 'vw') return 'Authorized by VW/Audi';
                  if (make === 'audi') return 'Authorized by VW/Audi';
                  if (make === 'porsche') return 'Porsche Partner Network';
                  if (make === 'bmw') return 'BMW Authorized Shop';
                  if (make === 'land rover') return 'Land Rover Authorized';
                  if (make === 'jaguar') return 'Jaguar Authorized';
                  return 'Factory-Authorized Diagnostics';
                })()}
              </p>
              <p className="text-[10px] text-gray-500 leading-tight mt-0.5">
                {(() => {
                  const make = (vehicle.make || '').toLowerCase();
                  if (['volkswagen', 'vw', 'audi'].includes(make)) return 'GRP & ODIS diagnostic access';
                  if (make === 'porsche') return 'PCSS & guided fault finding';
                  if (make === 'bmw') return 'Factory diagnostic systems';
                  return 'OEM-level tools & training';
                })()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
            <img
              src="/assets/award-plaque-square-150x150 copy.png"
              alt="Top 3 Award Winner 2026 - BusinessRate"
              className="w-10 h-10 flex-shrink-0 object-contain"
            />
            <div className="min-w-0">
              <p className="text-[12px] font-bold text-gray-900 leading-tight">Top 3 in Wise County</p>
              <p className="text-[10px] text-gray-500 leading-tight mt-0.5">BusinessRate 2026 Award</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 lg:gap-6 xl:gap-8 2xl:gap-10s">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 xl:col-span-3 2xl:col-span-4 space-y-8">
            {/* Image Gallery with Parallax */}
            <motion.div
              className="bg-white rounded-2xl n-royal shadow-2xl p-6 overflow-hidden"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}
              viewport={{ once: true }}
              data-scroll
              data-scroll-speed="0.8"
            >
              <ImageGallery
                images={vehicleImages}
                videos={vehicle.videos}
                selectedIndex={selectedImageIndex}
                onSelectImage={setSelectedImageIndex}
              />
            </motion.div>


            {/* Additional Vehicle Highlights - Only on XL+ screens */}
            <div className="hidden xl:block bg-white rounded-lg shadow-lg p-6 2xl:p-8 mb-6">
              <h3 className="text-xl 2xl:text-2xl font-bold mb-4">Vehicle Highlights</h3>
              <div className="grid grid-cols-2 2xl:grid-cols-3 gap-4">
                {vehicle.bodyType && (
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 2xl:w-16 2xl:h-16 bg-primary-100 rounded-full flex items-center justify-center">
                      <FaCar className="text-primary-600 text-xl 2xl:text-2xl" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg 2xl:text-xl">{vehicle.bodyType}</p>
                      <p className="text-sm 2xl:text-base text-gray-500">Body Style</p>
                    </div>
                  </div>
                )}
                {vehicle.engine && (
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 2xl:w-16 2xl:h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <FaGasPump className="text-green-600 text-xl 2xl:text-2xl" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg 2xl:text-xl">{vehicle.engine}</p>
                      <p className="text-sm 2xl:text-base text-gray-500">Engine</p>
                    </div>
                  </div>
                )}
                {vehicle.drivetrain && (
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 2xl:w-16 2xl:h-16 bg-blue-100 rounded-full flex items-center justify-center">
                      <FaTachometerAlt className="text-blue-600 text-xl 2xl:text-2xl" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg 2xl:text-xl">{vehicle.drivetrain}</p>
                      <p className="text-sm 2xl:text-base text-gray-500">Drivetrain</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Key Stats */}
            <div className="bg-white rounded-lg shadow-lg p-4 xl:p-6 2xl:p-8 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-8 gap-4 xl:gap-6">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-xl font-bold text-primary-600">{typeof vehicle.mileage === 'number' ? formatMileage(vehicle.mileage) : '—'}</div>
                  <div className="text-xs text-gray-600">Miles</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-xl font-bold text-primary-600">{vehicle.fuelType || '—'}</div>
                  <div className="text-xs text-gray-600">Fuel Type</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-xl font-bold text-primary-600">{vehicle.transmission || '—'}</div>
                  <div className="text-xs text-gray-600">Transmission</div>
                </div>
              </div>
            </div>

            {/* Content Area with Tabs */}
            <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 xl:p-8 2xl:p-10">
              {/* Description - Always Visible */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">Description</h3>
                  {loadingDescription && (
                    <div className="flex items-center text-sm text-gray-500">
                      <FiLoader className="w-4 h-4 animate-spin mr-2" />
                      <span>Generating enhanced description...</span>
                    </div>
                  )}
                  {aiDescription && (
                    <div className="flex items-center text-sm text-primary-600">
                      <FiEdit className="w-4 h-4 mr-1" />
                      <span>AI enhanced</span>
                    </div>
                  )}
                </div>
                <div className="prose prose-sm max-w-none">
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line">{displayDescription}</p>
                </div>
              </div>

              {/* Tabs */}
              <div className="mb-6 border-b border-gray-200">
                <div className="flex space-x-4 md:space-x-8 overflow-x-auto">
                  <button
                    onClick={() => setActiveTab('info')}
                    className={`pb-4 px-2 font-medium transition-colors ${activeTab === 'info'
                        ? 'border-b-2 border-primary-600 text-primary-600'
                        : 'text-gray-500 hover:text-gray-800'
                      }`}
                  >
                    Vehicle Details
                  </button>
                  <button
                    onClick={() => setActiveTab('qa')}
                    className={`pb-4 px-2 font-medium transition-colors ${activeTab === 'qa'
                        ? 'border-b-2 border-primary-600 text-primary-600'
                        : 'text-gray-500 hover:text-gray-800'
                      }`}
                  >
                    Ask Questions
                  </button>
                  <button
                    onClick={() => setActiveTab('financing')}
                    className={`pb-4 px-2 font-medium transition-colors ${activeTab === 'financing'
                        ? 'border-b-2 border-primary-600 text-primary-600'
                        : 'text-gray-500 hover:text-gray-800'
                      }`}
                  >
                    Financing
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              {activeTab === 'info' && (
                <div>
                  {/* Vehicle Specifications */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Vehicle Specifications</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-sm text-gray-600">Engine</span>
                          <span className="text-sm font-medium text-right">{vehicle.engine || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-sm text-gray-600">Drivetrain</span>
                          <span className="text-sm font-medium text-right">{vehicle.drivetrain || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-sm text-gray-600">Doors</span>
                          <span className="text-sm font-medium text-right">{vehicle.doors || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-sm text-gray-600">Seating</span>
                          <span className="text-sm font-medium text-right">{vehicle.seating || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-sm text-gray-600">Exterior Color</span>
                          <span className="text-sm font-medium text-right">{vehicle.exteriorColor || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-sm text-gray-600">Interior Color</span>
                          <span className="text-sm font-medium text-right">{vehicle.interiorColor || 'N/A'}</span>
                        </div>
                        {vehicle.vin && (
                          <div className="flex justify-between py-2 border-b border-gray-200">
                            <span className="text-sm text-gray-600">VIN / Serial</span>
                            <span className="text-sm font-medium text-right flex items-center gap-2">
                              <span className="font-mono break-all">{vehicle.vin}</span>
                              <button
                                type="button"
                                onClick={() => navigator.clipboard?.writeText(vehicle.vin)}
                                className="text-primary-600 hover:text-primary-700 text-xs"
                                title="Copy to clipboard"
                              >
                                Copy
                              </button>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Features & Options */}
                  {vehicle.features && vehicle.features.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Features & Options</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {vehicle.features.map((feature, index) => (
                          <div key={index} className="flex items-start space-x-2">
                            <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Warranty & History */}
                  <div className="grid pb-16 grid-cols-1 md:grid-cols-2 gap-6">
                    {vehicle.warranty && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Warranty</h4>
                        <p className="text-gray-700 text-sm">{vehicle.warranty}</p>
                      </div>
                    )}
                    {vehicle.history && (
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Vehicle History</h4>
                        <p className="text-gray-700 text-sm">{vehicle.history}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'qa' && (
                <div>
                  <VehicleQA vehicle={vehicle} />
                </div>
              )}

              {activeTab === 'financing' && (
                <div>
                  <FinancingCalculator vehiclePrice={vehicle.price} />
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Sticky Pricing and Contact */}
          <div className="lg:col-span-1 xl:col-span-1 2xl:col-span-1">
            {/* Pricing Card with Parallax */}
            <motion.div
              className="n-royal rounded-2xl shadow-2xl p-6 xl:p-8 sticky top-24 overflow-hidden sticky-parallax"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}
              viewport={{ once: true }}
              data-scroll
              data-scroll-speed="0.5"
            >
              {/* Animated Background Pattern */}
              <div className="absolute inset-0 opacity-50">
                <div className="absolute inset-0" style={{
                  backgroundImage: `repeating-linear-gradient(
                    45deg,
                    transparent,
                    transparent 10px,
                    rgba(0,0,0,0.03) 10px,
                    rgba(0,0,0,0.03) 20px
                  )`
                }} />
              </div>

              <div className="relative z-10 n-royal">
                <div className="text-center mb-4">
                  <p className="text-gray-600 mb-2 text-base xl:text-lg">Our Price</p>
                  <motion.p
                    className="text-2xl xl:text-3xl font-bold n-royal break-words"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    viewport={{ once: true }}
                  >
                    ${vehicle.price.toLocaleString()}
                  </motion.p>
                  <p className="text-xs xl:text-sm text-gray-500 mt-2">
                    Plus tax, title, and fees
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  <motion.a
                    href="tel:8173822316"
                    className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 px-4 rounded-lg text-sm font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <FaPhone className="w-4 h-4" />
                    <span>Call (817) 382-2316</span>
                  </motion.a>
                  <motion.button
                    onClick={() => navigate('/finance-application')}
                    className="w-full border-2 border-primary-600 text-primary-600 py-3 px-4 rounded-lg text-sm font-semibold hover:bg-primary-50 transition-all duration-300 flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <FaCalculator className="w-4 h-4" />
                    <span>Get Financing</span>
                  </motion.button>
                  <motion.button
                    onClick={() => setShowLeadForm(true)}
                    className="w-full border-2 border-gray-400 text-gray-700 py-3 px-4 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <FaEnvelope className="w-4 h-4" />
                    <span>Contact Us</span>
                  </motion.button>
                  {/* For Golf Carts - Show both purchase and deposit options */}
                  {vehicle.vehicleType === 'golf-cart' ? (
                    <>
                      <motion.button
                        onClick={() => {
                          setPaymentType('full');
                          setShowPaymentModal(true);
                        }}
                        className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <FaDollarSign className="w-4 h-4" />
                        <span>Purchase - ${vehicle.price?.toLocaleString()}</span>
                      </motion.button>
                      <motion.button
                        onClick={() => {
                          setPaymentType('deposit');
                          setShowPaymentModal(true);
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <FaDollarSign className="w-4 h-4" />
                        <span>Reserve with ${(vehicle.deposit_amount || 500)?.toLocaleString()} Deposit</span>
                      </motion.button>
                    </>
                  ) : (
                    /* For regular vehicles - Show only deposit option */
                    <motion.button
                      onClick={() => setShowPaymentModal(true)}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <FaDollarSign className="w-4 h-4" />
                      <span>Make Deposit</span>
                    </motion.button>
                  )}
                </div>
              </div>

              {/* Dealership Info — Google VLA recommends phone, directions, hours */}
              <div className="mt-6 pt-6 border-t border-gray-200 relative z-10">
                <h4 className="font-semibold text-gray-900 text-sm mb-3">Dealership</h4>
                <div className="space-y-2 text-xs text-gray-600">
                  <p className="font-medium text-gray-800">Paradise-Performance</p>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Paradise+Performance+305+Co+Rd+3470+Paradise+TX+76073"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2 hover:text-primary-600 transition-colors"
                  >
                    <FiMapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>305 Co Rd 3470, Paradise, TX 76073</span>
                  </a>
                  <a href="tel:8173822316" className="flex items-center gap-2 hover:text-primary-600 transition-colors">
                    <FiPhone className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>(817) 382-2316</span>
                  </a>
                  <div className="flex items-start gap-2">
                    <FiClock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <div>
                      <p>Mon-Fri: 9 AM - 5 PM</p>
                      <p>Sat: 10 AM - 2 PM (Sales)</p>
                      <p>Sun: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="mt-4 pt-4 border-t border-gray-200 relative z-10">
                <div className="text-xs text-gray-500">
                  *Payment estimate based on 72-month financing at 4.9% APR with approved credit.
                  Actual terms may vary.
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Lead Form Modal */}
      {showLeadForm && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">
                Contact Us About This Vehicle
              </h2>
              <button
                onClick={() => setShowLeadForm(false)}
                className="text-gray-500 hover:text-gray-700 text-3xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <LeadForm
                vehicleInfo={{
                  year: vehicle?.year,
                  make: vehicle?.make,
                  model: vehicle?.model,
                  vin: vehicle?.vin || vehicle?.id,
                  price: vehicle?.price
                }}
                leadType="vehicle-inquiry"
                onClose={() => setShowLeadForm(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Calendar Scheduler Modal */}
      {showCalendar && (
        <CalendarScheduler
          isOpen={showCalendar}
          onClose={() => setShowCalendar(false)}
          vehicleInfo={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentType('deposit'); // Reset to default
          }}
          vehicle={vehicle}
          paymentType={paymentType}
        />
      )}
    </div>
  );
};

export default VehicleDetail;